import {useState} from 'react';
import {useRouter} from 'next/router';
import Image from 'next/legacy/image';
import {Flex, InputGroup, InputLeftElement, Input, Button, Text, useToast, Box} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {PhoneIcon} from '@eksab/assets/icons';

import type {PackageType} from '@eksab/features/store/types';

import {useBuyPackageMutation} from '../hooks';

interface EWalletPaymentProps {
  packageId: number;
  code: string;
  type: PackageType;
}

export const EWalletPayment = ({packageId, code, type}: EWalletPaymentProps) => {
  const [eWalletNumber, setEWalletNumber] = useState('');
  const {push} = useRouter();
  const toast = useToast();

  const t = useTranslate();
  const buyPackageMutation = useBuyPackageMutation(
    {
      package_id: packageId,
      package_type: type,
      payment_method: 'WALLET',
      phone_number: eWalletNumber,
      discount_voucher_code: code || undefined,
    },
    {
      onSuccess: (res) => res.next.action !== 'add-coins' && push(res.next.url),
      onError: (e) => void toast({title: e.response?.data?.message ?? t('app.errors.try-again'), status: 'error'}),
    },
  );

  const onPay = (e: React.FormEvent<HTMLFormElement | HTMLDivElement>) => {
    e.preventDefault();

    mixpanel?.track('Button Clicked', {button: 'payment.ewallet.pay_now'});
    fbq?.('track', 'InitiateCheckout');

    buyPackageMutation.mutate();
  };

  return (
    <>
      <Flex
        as="form"
        w="full"
        flexDir="column"
        justify="space-between"
        align="center"
        gap="4"
        shadow="xs"
        rounded="md"
        mt="3"
        px={[6, null, 5]}
        pt="5"
        pb="4"
        onSubmit={onPay}
      >
        <Flex alignItems="center" textAlign="center">
          <Flex flexDir="column" alignItems="center">
            <Image width="32" height="32" alt="Vodafone Cash" src="/images/wallet/vf.png" />
            <Text color="#E60000" fontWeight="bold" fontSize="12">
              Vodafone Cash
            </Text>
          </Flex>
          <Box w="1px" h="70%" bgColor="#8F8F8F" mx="3" />
          <Flex flexDir="column" alignItems="center">
            <Image width="32" height="32" alt="We Pay" src="/images/wallet/we.png" />
            <Text color="#752573" fontWeight="bold" fontSize="12">
              We Pay
            </Text>
          </Flex>
          <Box w="1px" h="70%" bgColor="#8F8F8F" mx="3" />
          <Flex flexDir="column" alignItems="center">
            <Image width="32" height="32" alt="Orange Pay" src="/images/wallet/orange.png" />
            <Text color="#EF6924" fontWeight="bold" fontSize="12">
              Orange Pay
            </Text>
          </Flex>
          <Box w="1px" h="70%" bgColor="#8F8F8F" mx="3" />
          <Flex flexDir="column" alignItems="center">
            <Image width="32" height="32" alt="Etisalat Cash" src="/images/wallet/etisalat.png" />
            <Text color="#131313" fontWeight="bold" fontSize="12">
              Etisalat Cash
            </Text>
          </Flex>
        </Flex>

        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <PhoneIcon />
          </InputLeftElement>

          <Input
            type="tel"
            autoComplete="tel"
            placeholder={t('payment.method.e-wallet.number')}
            value={eWalletNumber}
            onChange={(e) => setEWalletNumber(e.target.value)}
          />
        </InputGroup>

        <Button w="full" type="submit" mt="1" isLoading={buyPackageMutation.isLoading}>
          {t('payment.method.e-wallet.cta')}
        </Button>
      </Flex>
    </>
  );
};
